
import React, { useEffect } from "react";
import { Route, Switch, Redirect, Link, useRouteMatch } from "react-router-dom";
import { BreadcrumbItem } from "reactstrap";
import { Navbar, NavbarProvider } from "@repoflow/components/navbar";
import {
  Sidebar,
  SidebarProvider
} from "@repoflow/components/sidebar-menu";
import { useAccount } from "@repoflow/components/account";
import { basename } from "env";
import * as AuthRoute from "./auth";
import * as DashboardRoute from "./dashboard";
import * as PreviewRoute from "./preview";
import * as SitesRoute from "./sites";
import * as KeysRoute from "./keys";
import * as AccountRoute from "./account";
import { siteSubmenu, siteIdOf } from "./sidebar";

// Sites is still the section you work in — a page belongs to one, so there is
// nowhere sensible to land before you have picked which — and the root keeps
// going there rather than to the summary.
const RootRoute = () => <Redirect to={`/site`} />;

// Does this location need somebody signed in?
//
// `/auth` is where signing in HAPPENS, so redirecting there from there is a
// loop. `/preview` is the editor's frame, and since P142 it is served from the
// sites hostname — an origin that has no session and must never have one,
// because it is the origin a tenant's `<script>` runs on. It needs no account:
// it renders nothing until `mount` arrives, the editor does every read, and the
// payload is checked against the editor's origin before it is touched.
//
// Exported for the test. The bug this closes was the `/preview` clause being
// absent, which sent the frame to `/auth/home` before it could announce itself
// and emptied the preview pane on every screen that has one — twelve e2e specs,
// and nothing below the e2e level saw it.
// WHOLE SEGMENTS, not a prefix. `startsWith` would exempt `/app/previews-report`
// along with `/app/preview`, and this rule's falsy case is "no sign-in needed" —
// the shape that should never be reachable by writing a route whose name merely
// begins the same way. The same helper covers `/auth`, which had the looser
// form before this and is the same rule rather than a second change.
const under = (pathname, route) => {
  const base = `${basename}${route}`;
  return pathname === base || pathname.startsWith(`${base}/`);
};

export const needsSignIn = (account, pathname) => {
  const path = String(pathname);
  return !account && !under(path, "/auth") && !under(path, "/preview");
};

function Routes() {
  const account = useAccount();

  // The site you are in, if you are in one — see ./sidebar for what hangs off
  // it, and for why `/site/new` is not one.
  const siteId = siteIdOf(useRouteMatch({ path: `/site/:siteId` }));

  // Signed out, go and sign in — except on the two routes that are not for a
  // signed-in person to look at. See `needsSignIn`, which is exported so the
  // rule can be tested as the boolean it is: the defect was a missing clause in
  // it, and a missing clause is invisible to a render test of the happy path.
  //
  // `/preview` IS THE SECOND ONE SINCE P142, and leaving it out was the whole
  // of why the cross-origin frame showed nothing. The frame is served from the
  // sites hostname, which has no session and never will: the cookie belongs to
  // the app's origin, and giving that origin's session to the document tenant
  // markup runs in is the exact thing P142 removed. So the frame arrived with
  // no account, this effect sent it to `/auth/home`, and the preview route
  // unmounted before it could announce itself — twelve e2e specs, every one of
  // them an empty pane.
  //
  // It needs no account. It renders nothing until `mount` arrives, the editor
  // does every read, and the payload is checked against the editor's origin
  // before it is touched. A sign-in here would authenticate the one document
  // that must not be authenticated.
  useEffect(() => {
    if (needsSignIn(account, window.location.pathname)) {
      window.location.href = `${basename}/auth/home`;
    }
  }, [account]);

  return (
    <Switch>
      <Route path={`/auth`} component={AuthRoute.Content} />
      {/* Above the chrome, and deliberately bare: this is the document the
          page editor's preview pane loads into its iframe, and it must carry
          no navbar, no sidebar and no breadcrumb. See ./preview. */}
      <Route path={`/preview`} component={PreviewRoute.Content} />
      {/* No `component` prop here: in react-router v5 children take
          precedence over it, so one was always dead code. */}
      <Route path={`/`}>
        <NavbarProvider>
          <SidebarProvider>
            <Navbar title={"Repoflow Pages"} menu={[]} />

            <Sidebar
              menu={[
                {
                  icon: "tachometer",
                  label: "Dashboard",
                  to: `/dashboard`,
                },
                {
                  icon: "sitemap",
                  label: "Sites",
                  to: `/site`,
                  // Only while one is selected. A submenu that is empty until
                  // you pick a site would be a caret that does nothing, and the
                  // shell has no site to name before then.
                  ...(siteId ? { submenu: siteSubmenu(siteId) } : {}),
                },
                {
                  icon: "key",
                  label: "API Keys",
                  to: `/key`,
                }/*,
            {
              icon: "cogs",
              label: "Configuration",
              to: `/config`,
            },*/
              ]}
            >
              <ol className="breadcrumb m-0 small">
                <BreadcrumbItem>
                  <Link to={`/`}>
                    <i className="fa fa-home"></i>
                  </Link>
                </BreadcrumbItem>
                <Switch>
                  <Route path={`/account`} component={AccountRoute.Indice} />
                  <Route path={`/dashboard`} component={DashboardRoute.Indice} />
                  <Route path={`/site`} component={SitesRoute.Indice} />
                  <Route path={`/key`} component={KeysRoute.Indice} />
                </Switch>
              </ol>

              <Switch>
                <Route path={`/account`} component={AccountRoute.Content} />
                <Route path={`/dashboard`} component={DashboardRoute.Content} />
                <Route path={`/site`} component={SitesRoute.Content} />
                <Route path={`/key`} component={KeysRoute.Content} />
                <Route path={`/`} component={RootRoute} />
              </Switch>
            </Sidebar>
          </SidebarProvider>
        </NavbarProvider>
      </Route>
    </Switch>
  );
}

export default Routes;
